import { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuthUrls } from '../config/auth';
import { cognitoService } from '../services/cognitoService';
import {
    User,
    storeTokens,
    getStoredTokens,
    clearTokens,
    isTokenExpired,
    isTokenExpiringSoon,
    getTokenExpirationTime,
    extractUserFromIdToken,
} from '../utils/authUtils';

interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: User | null;
    error: string | null;
}

type AuthAction =
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_AUTHENTICATED'; payload: User }
    | { type: 'SET_ERROR'; payload: string }
    | { type: 'CLEAR_ERROR' }
    | { type: 'SIGN_OUT' };

interface AuthContextValue extends AuthState {
    signIn: () => void;
    signUp: () => void;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function authReducer(state: AuthState, action: AuthAction): AuthState {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_AUTHENTICATED':
            return { isAuthenticated: true, isLoading: false, user: action.payload, error: null };
        case 'SET_ERROR':
            return { ...state, isLoading: false, error: action.payload };
        case 'CLEAR_ERROR':
            return { ...state, error: null };
        case 'SIGN_OUT':
            return { isAuthenticated: false, isLoading: false, user: null, error: null };
        default:
            return state;
    }
}

const initialState: AuthState = {
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const navigate = useNavigate();
    const location = useLocation();
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    const initializedRef = useRef(false);

    const setupRefreshTimer = () => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }

        const tokens = getStoredTokens();
        if (!tokens) return;

        const expirationTime = getTokenExpirationTime(tokens.accessToken);
        const refreshTime = expirationTime - Date.now() - 5 * 60 * 1000;

        if (refreshTime <= 0) {
            refreshTokensProactively();
            return;
        }

        refreshTimerRef.current = setTimeout(() => {
            refreshTokensProactively();
        }, refreshTime);
    };

    const refreshTokensProactively = async () => {
        const tokens = getStoredTokens();
        if (!tokens) return;

        try {
            const newTokens = await cognitoService.refreshTokens(tokens.refreshToken);
            storeTokens(newTokens);
            setupRefreshTimer();
        } catch {
            clearTokens();
            dispatch({ type: 'SIGN_OUT' });
        }
    };

    useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;

        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
            dispatch({ type: 'SET_ERROR', payload: errorDescription || error });
            dispatch({ type: 'SET_LOADING', payload: false });
            return;
        }

        if (code && location.pathname === '/auth/callback') {
            handleCodeExchange(code);
            return;
        }

        restoreSession();
    }, []);

    const handleCodeExchange = async (code: string) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const tokens = await cognitoService.exchangeCodeForTokens(code);
            storeTokens(tokens);

            const user = extractUserFromIdToken(tokens.id_token);
            if (user) {
                dispatch({ type: 'SET_AUTHENTICATED', payload: user });
                setupRefreshTimer();
                navigate('/dashboard', { replace: true });
            } else {
                throw new Error('Failed to extract user from token');
            }
        } catch (err) {
            dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Authentication failed' });
        }
    };

    const restoreSession = async () => {
        const tokens = getStoredTokens();
        if (!tokens) {
            dispatch({ type: 'SET_LOADING', payload: false });
            return;
        }

        if (isTokenExpired(tokens.accessToken)) {
            if (isTokenExpiringSoon(tokens.refreshToken, 0)) {
                clearTokens();
                dispatch({ type: 'SET_LOADING', payload: false });
                return;
            }

            try {
                const newTokens = await cognitoService.refreshTokens(tokens.refreshToken);
                storeTokens(newTokens);
                const user = extractUserFromIdToken(newTokens.id_token);
                if (user) {
                    dispatch({ type: 'SET_AUTHENTICATED', payload: user });
                    setupRefreshTimer();
                } else {
                    clearTokens();
                    dispatch({ type: 'SET_LOADING', payload: false });
                }
            } catch {
                clearTokens();
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        } else {
            const user = extractUserFromIdToken(tokens.idToken);
            if (user) {
                dispatch({ type: 'SET_AUTHENTICATED', payload: user });
                setupRefreshTimer();
            } else {
                clearTokens();
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        }
    };

    useEffect(() => {
        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        };
    }, []);

    const signIn = () => {
        const urls = getAuthUrls();
        window.location.href = urls.signIn;
    };

    const signUp = () => {
        const urls = getAuthUrls();
        window.location.href = urls.signUp;
    };

    const signOut = async () => {
        try {
            const tokens = getStoredTokens();
            if (tokens) {
                await cognitoService.revokeToken(tokens.accessToken);
            }
        } catch {
            // Best-effort revocation
        }
        clearTokens();
        dispatch({ type: 'SIGN_OUT' });
        const urls = getAuthUrls();
        window.location.href = urls.signOut;
    };

    return <AuthContext.Provider value={{ ...state, signIn, signUp, signOut }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
